/**
 * Calculate the Levenshtein distance between two strings
 * @param str1 - First string
 * @param str2 - Second string
 * @returns The Levenshtein distance
 */
export const levenshteinDistance = (str1: string, str2: string): number => {
  if (!str1 || !str2) return Math.max(str1?.length || 0, str2?.length || 0);

  const matrix: number[][] = [];

  // Initialize the matrix
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
};

/**
 * Normalize a string for comparison (remove diacritics, lowercase, trim)
 * @param str - String to normalize
 * @returns Normalized string
 */
export const normalizeString = (str: string): string => {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics
};

/**
 * Calculate similarity ratio between two strings (0-1)
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Similarity ratio (0 = completely different, 1 = identical)
 */
export const similarityRatio = (str1: string, str2: string): number => {
  if (!str1 || !str2) return 0;

  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;

  const distance = levenshteinDistance(str1, str2);
  return (maxLength - distance) / maxLength;
};
